import React, { useState, useEffect, useCallback } from 'react';
import { Post, Follow, AppStats, AuthStatus, PostType } from './types';
import FeedView from './components/FeedView';
import AdminView from './components/AdminView';
import LoginView from './components/LoginView';
import SetupView from './components/SetupView';

export const THEME_COLORS: Record<string, { primary: string; accent: string }> = {
  indigo: { primary: '#4f46e5', accent: '#6366f1' },
  rose: { primary: '#e11d48', accent: '#fb7185' },
  emerald: { primary: '#10b981', accent: '#34d399' },
  teal: { primary: '#0d9488', accent: '#14b8a6' },
  amber: { primary: '#f59e0b', accent: '#fbbf24' },
  sky: { primary: '#0ea5e9', accent: '#38bdf8' },
  violet: { primary: '#8b5cf6', accent: '#a78bfa' },
  slate: { primary: '#475569', accent: '#64748b' },
  orange: { primary: '#f97316', accent: '#fb923c' },
  pink: { primary: '#db2777', accent: '#f472b6' },
};

const BrandLogo: React.FC<{ isRefreshing?: boolean }> = ({ isRefreshing }) => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={`drop-shadow-sm transition-transform duration-700 ${isRefreshing ? 'animate-spin' : ''}`}>
    <defs>
      <linearGradient id="brandGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
        <stop stopColor="var(--theme-primary)" />
        <stop offset="1" stopColor="var(--theme-accent)" />
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="10" fill="url(#brandGrad)" />
    <path d="M11 19.5V21H12.5L19.5 14L18 12.5L11 19.5ZM21.5 12L20 10.5L21 9.5C21.3 9.2 21.7 9.2 22 9.5L22.5 10C22.8 10.3 22.8 10.7 22.5 11L21.5 12Z" fill="white" />
  </svg>
);

const App: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [follows, setFollows] = useState<Follow[]>([]);
  const [stats, setStats] = useState<AppStats>({ postCount: 0, followCount: 0, totalLikes: 0, mediaSizeMb: 0 });
  const [authStatus, setAuthStatus] = useState<AuthStatus>({ isSetup: false, isAuthenticated: false });
  const [view, setView] = useState<'feed' | 'admin' | 'login'>('feed');
  const [themeColor, setThemeColor] = useState('indigo');
  const [isSystemLoading, setIsSystemLoading] = useState(true);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [isFeedRefreshing, setIsFeedRefreshing] = useState(false);
  
  // Track likes locally for guests
  const [localLikes, setLocalLikes] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('yoursite-local-likes');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('yoursite-theme') === 'dark' || 
             (!localStorage.getItem('yoursite-theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem('yoursite-local-likes', JSON.stringify(localLikes));
  }, [localLikes]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('yoursite-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('yoursite-theme', 'light');
    }
  }, [isDarkMode]);

  const fetchAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch('/api/auth/status', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setAuthStatus(data);
      }
    } catch (e) {
      console.error("[App] Failed to fetch auth status", e);
    } finally {
      setIsSystemLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('yoursite-token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    try {
      const [postsRes, followsRes, settingsRes] = await Promise.all([
        fetch('/api/posts', { headers }),
        authStatus.isAuthenticated ? fetch('/api/follows', { headers }) : Promise.resolve(null),
        fetch('/api/settings', { headers }) // Always fetch public settings
      ]);

      if (postsRes && postsRes.ok) {
        const postsData = await postsRes.json();
        setPosts(postsData);
      }
      
      if (followsRes && followsRes.ok) {
        const followsData = await followsRes.json();
        setFollows(followsData);
      }

      if (settingsRes && settingsRes.ok) {
        const settings = await settingsRes.json();
        if (settings.theme_color) setThemeColor(settings.theme_color);
      }
      
      const localPostCount = posts.filter(p => !p.sourceUrl).length;
      setStats({
        postCount: localPostCount,
        followCount: follows.length,
        totalLikes: posts.reduce((acc, p) => acc + (p.likeCount || 0), 0),
        mediaSizeMb: 0
      });
    } catch (e) {
      console.error("[App] Data fetch failed", e);
    } finally {
      setIsInitialDataLoading(false);
    }
  }, [authStatus.isAuthenticated, posts.length, follows.length]);

  const handleRefresh = useCallback(async () => {
    if (isFeedRefreshing) return;
    setIsFeedRefreshing(true);
    
    const token = localStorage.getItem('yoursite-token');
    try {
      if (authStatus.isAuthenticated) {
        await fetch('/api/follows/refresh', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      await fetchData();
    } catch (e) {
      console.error("[App] Manual refresh failed", e);
    } finally {
      setIsFeedRefreshing(false);
    }
  }, [isFeedRefreshing, authStatus.isAuthenticated, fetchData]);

  useEffect(() => {
    fetchAuthStatus();
  }, [fetchAuthStatus]);

  useEffect(() => {
    if (!isSystemLoading && authStatus.isSetup) {
      fetchData();
    } else if (!isSystemLoading && !authStatus.isSetup) {
      setIsInitialDataLoading(false);
    }
  }, [fetchData, isSystemLoading, authStatus.isSetup]);

  const handleLike = async (id: string) => {
    const post = posts.find(p => p.id === id);
    if (!post) return;

    // Determine if we are unliking based on either the owner flag (if logged in) or local storage (if guest)
    const isLikedByMe = localLikes.includes(id);
    const isLikedByAdmin = !!post.isLikedByOwner;
    const shouldUnlike = authStatus.isAuthenticated ? isLikedByAdmin : isLikedByMe;
    
    const method = shouldUnlike ? 'unlike' : 'like';
    const token = localStorage.getItem('yoursite-token');
    
    try {
      const res = await fetch(`/api/posts/${id}/${method}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        // Update local tracking
        if (shouldUnlike) {
          setLocalLikes(prev => prev.filter(x => x !== id));
        } else {
          setLocalLikes(prev => [...prev, id]);
        }
        fetchData();
      }
    } catch (e) {
      console.error("[App] Like/unlike failed", e);
    }
  };

  const handlePostCreate = async (formData: FormData) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch('/api/posts', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        await fetchData();
        setView('feed');
        return true;
      }
    } catch (e) {
      console.error("[App] Post creation failed", e);
    }
    return false;
  };

  const handleDeletePost = async (id: string) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch(`/api/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
        return true;
      }
    } catch (e) {
      console.error("[App] Post deletion failed", e);
    }
    return false;
  };

  const handleUpdatePost = async (id: string, updates: Partial<Post>) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch(`/api/posts/${id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        await fetchData();
        return true;
      }
    } catch (e) {
      console.error("[App] Post update failed", e);
    }
    return false;
  };

  const handleFollowAdd = async (follow: { url: string; label: string }) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch('/api/follows', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(follow)
      });
      if (res.ok) {
        fetchData();
        return true;
      }
    } catch (e) {
      console.error("[App] Follow failed", e);
    }
    return false;
  };

  const handleFollowUpdate = async (follow: Follow) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch(`/api/follows/${follow.id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          weight: follow.weight,
          muted: follow.muted,
          blocked: follow.blocked
        })
      });
      if (res.ok) fetchData();
    } catch (e) {
      console.error("[App] Follow update failed", e);
    }
  };

  const handleFollowDelete = async (id: string) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      const res = await fetch(`/api/follows/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchData();
    } catch (e) {
      console.error("[App] Unfollow failed", e);
    }
  };

  const handleLoginSuccess = (token: string, remember: boolean) => {
    localStorage.setItem('yoursite-token', token);
    setAuthStatus(prev => ({ ...prev, isAuthenticated: true }));
    setView('admin');
    fetchAuthStatus();
  };

  const handleLogout = () => {
    localStorage.removeItem('yoursite-token');
    setAuthStatus(prev => ({ ...prev, isAuthenticated: false }));
    setView('feed');
  };

  const isLoading = isSystemLoading || (authStatus.isSetup && isInitialDataLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center transition-colors duration-500">
        <BrandLogo />
        <div className="flex gap-1.5 mt-6">
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-600/30 dark:bg-indigo-400/30 animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-600/30 dark:bg-indigo-400/30 animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-600/30 dark:bg-indigo-400/30 animate-bounce"></div>
        </div>
      </div>
    );
  }

  if (!authStatus.isSetup) {
    return <SetupView onSetupSuccess={(token) => {
      localStorage.setItem('yoursite-token', token);
      fetchAuthStatus();
    }} />;
  }

  const themeConfig = THEME_COLORS[themeColor] || THEME_COLORS.indigo;

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans`}>
      <style>{`
        :root { 
          --theme-primary: ${themeConfig.primary}; 
          --theme-accent: ${themeConfig.accent}; 
          --theme-soft: color-mix(in srgb, ${themeConfig.primary}, transparent 90%);
        }
        .text-indigo-600 { color: var(--theme-primary) !important; }
        .bg-indigo-600 { background-color: var(--theme-primary) !important; }
        .border-indigo-600 { border-color: var(--theme-primary) !important; }
        .focus\\:ring-indigo-500:focus { --tw-ring-color: var(--theme-primary) !important; }
        .bg-indigo-50 { background-color: var(--theme-soft) !important; }
        .nav-pill-active { left: ${view === 'admin' ? 'calc(50% + 1px)' : '2px'}; }
      `}</style>

      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer group" 
            onClick={() => {
              if (view === 'feed') {
                handleRefresh();
              } else {
                setView('feed');
                handleRefresh();
              }
            }}
          >
            <BrandLogo isRefreshing={isFeedRefreshing} />
            <div className="flex items-baseline gap-2">
              <h1 className={`font-black text-xl tracking-tighter gradient-text ${isFeedRefreshing ? 'animate-pulse' : ''}`}>YourSite</h1>
              {authStatus.ownerName && (
                <span className="text-gray-400 dark:text-gray-500 font-medium text-sm border-l border-gray-200 dark:border-gray-800 pl-2">
                  {authStatus.ownerName}
                </span>
              )}
            </div>
          </div>
          
          <nav className="flex items-center gap-4">
            {authStatus.isAuthenticated && (
              <div className="relative flex p-1 bg-gray-100 dark:bg-slate-900 rounded-2xl w-40 h-10 overflow-hidden">
                <div className="nav-pill-active absolute top-1 bottom-1 w-[calc(50%-3px)] bg-white dark:bg-slate-800 rounded-xl shadow-sm transition-all duration-300 ease-out"></div>
                <button 
                  onClick={() => setView('feed')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${view === 'feed' ? 'gradient-text' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  <i className={`fa-solid fa-house ${view === 'feed' ? 'text-indigo-600' : ''}`}></i> Feed
                </button>
                <button 
                  onClick={() => setView('admin')}
                  className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${view === 'admin' ? 'gradient-text' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  <i className={`fa-solid fa-user ${view === 'admin' ? 'text-indigo-600' : ''}`}></i> Me
                </button>
              </div>
            )}

            {!authStatus.isAuthenticated && (
              <button 
                onClick={() => setView('login')}
                className="px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white shadow-lg shadow-indigo-500/20 hover:scale-105 active:scale-95 transition-all"
              >
                Login
              </button>
            )}

            <button 
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-slate-900 text-gray-400 hover:text-indigo-600 transition-all"
              title="Toggle Dark Mode"
            >
              <i className={`fa-solid ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {view === 'feed' && (
          <FeedView 
            posts={posts} 
            onLike={handleLike} 
            isAuthenticated={authStatus.isAuthenticated}
            localLikes={localLikes}
            onLoginRequest={() => setView('login')}
            onDeletePost={handleDeletePost}
            onUpdatePost={handleUpdatePost}
          />
        )}
        {view === 'admin' && authStatus.isAuthenticated && (
          <AdminView 
            posts={posts}
            follows={follows}
            stats={stats}
            onPostCreate={handlePostCreate}
            onFollowAdd={handleFollowAdd}
            onFollowUpdate={handleFollowUpdate}
            onFollowDelete={handleFollowDelete}
            onLogout={handleLogout}
            themeColor={themeColor}
            onThemeColorChange={setThemeColor}
          />
        )}
        {view === 'login' && (
          <LoginView 
            onLoginSuccess={handleLoginSuccess}
            onCancel={() => setView('feed')}
          />
        )}
      </main>
    </div>
  );
};

export default App;